import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { Modal, Spin, message, notification } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { useAuthStore } from '@/stores/authStore';
import { useAppStore } from '@/stores/appStore';
import { StagingEnvironmentBanner } from '@/components/StagingEnvironmentBanner';
import { StaffAuthenticatedShell, StaffLoginShell } from './StaffShell';
import './App.css';
import './styles/staffResponsive.css';
const App = () => {
    const { isAuthenticated, user, loading: authLoading, checkAuth } = useAuthStore();
    const { appReady, initializeApp } = useAppStore();
    const [initializing, setInitializing] = useState(true);
    console.log('[App] render', { initializing, authLoading, appReady, isAuthenticated });
    useEffect(() => {
        let cancelled = false;
        const init = async () => {
            try {
                await checkAuth();
                await initializeApp();
                if (!cancelled) {
                    const u = useAuthStore.getState().user;
                    const authed = useAuthStore.getState().isAuthenticated;
                    if (authed && u) {
                        message.success(`欢迎回来，${u.realName || u.username}！`);
                    }
                }
            }
            catch (error) {
                console.error('应用初始化失败:', error);
                notification.error({
                    message: '应用初始化失败',
                    description: '请检查网络连接或联系管理员',
                });
            }
            finally {
                if (!cancelled) {
                    setInitializing(false);
                }
            }
        };
        void init();
        return () => {
            cancelled = true;
        };
    }, [checkAuth, initializeApp]);
    useEffect(() => {
        const electronAPI = window.electronAPI;
        if (electronAPI) {
            const handleNewOrder = () => {
                if (isAuthenticated) {
                    window.location.hash = '#/orders/new';
                }
            };
            const handleShowAbout = () => {
                notification.info({
                    message: '关于珠宝定制管理系统',
                    description: '版本 1.0.0 © 2024 珠宝定制工作室',
                    duration: 5,
                });
            };
            electronAPI.onMenuNewOrder(handleNewOrder);
            electronAPI.onShowAbout(handleShowAbout);
            return () => {
                electronAPI.removeMenuNewOrderListener(handleNewOrder);
                electronAPI.removeShowAboutListener(handleShowAbout);
            };
        }
    }, [isAuthenticated]);
    useEffect(() => {
        const electronAPI = window.electronAPI;
        if (!electronAPI || !isAuthenticated) {
            return;
        }
        electronAPI.checkForUpdates().catch((e) => {
            console.error('[updater] 主动检查更新失败', e);
        });
        const onAvailable = (event) => {
            const detail = event.detail || {};
            notification.info({
                message: '发现新版本',
                description: `检测到新版本 ${detail.version || ''}，正在后台下载...`,
                duration: 5,
            });
        };
        const onDownloaded = (event) => {
            const detail = event.detail || {};
            Modal.confirm({
                title: '新版本已下载完成',
                content: `版本 ${detail.version || ''} 已准备就绪，是否立即重启并安装更新？`,
                okText: '立即安装',
                cancelText: '稍后',
                onOk: () => electronAPI.quitAndInstallUpdate(),
            });
        };
        const onError = (event) => {
            const detail = event.detail || {};
            notification.warning({
                message: '更新检查失败',
                description: detail.message || '暂时无法获取更新信息',
                duration: 4,
            });
        };
        const onProgress = (event) => {
            const detail = event.detail || {};
            if (typeof detail.percent === 'number') {
                const p = Math.round(detail.percent);
                if (p > 0 && p % 25 === 0) {
                    message.loading({ content: `更新下载中 ${p}%`, key: 'update-progress', duration: 1 });
                }
                if (p >= 100) {
                    message.destroy('update-progress');
                }
            }
        };
        window.addEventListener('electron-update-available', onAvailable);
        window.addEventListener('electron-update-downloaded', onDownloaded);
        window.addEventListener('electron-update-error', onError);
        window.addEventListener('electron-update-download-progress', onProgress);
        return () => {
            window.removeEventListener('electron-update-available', onAvailable);
            window.removeEventListener('electron-update-downloaded', onDownloaded);
            window.removeEventListener('electron-update-error', onError);
            window.removeEventListener('electron-update-download-progress', onProgress);
            message.destroy('update-progress');
        };
    }, [isAuthenticated]);
    if (initializing || authLoading || !appReady) {
        console.log('[App] show initializing screen');
        return (_jsxs("div", { className: "app-router-fill app-router-fill--scroll", children: [_jsx(StagingEnvironmentBanner, {}), _jsx("div", { style: {
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        flex: 1,
                        minHeight: 240,
                        backgroundColor: '#f0f2f5',
                    }, children: _jsx(Spin, { indicator: _jsx(LoadingOutlined, { style: { fontSize: 48 }, spin: true }), tip: "\u6B63\u5728\u521D\u59CB\u5316\u5E94\u7528...", size: "large" }) })] }));
    }
    if (!isAuthenticated) {
        console.log('[App] not authenticated -> login router');
        return _jsx(StaffLoginShell, {});
    }
    console.log('[App] authenticated -> main router', user?.username);
    return _jsx(StaffAuthenticatedShell, {});
};
export default App;
